<template>
    <PageHeader :title="form.updateStatus ? sprintf($t('pages.ecommerce.subscription.editSubscriptionPattern'), [form.data.id ?? '']) : $t('pages.ecommerce.subscription.newSubscription')" back-to="/ecommerce/subscription"/>

    <el-tabs v-model="tabModel" class="subscription-tabs">
        <el-tab-pane :label="$t('pages.ecommerce.subscription.save.tabs.general')" name="general">
            <el-form :model="form.data" ref="dataForm">
                <div class="row g-6 mb-6 g-xl-9 mb-xl-9 unset-position-card">
                    <div class="col-md-9">
                        <div class="card mb-5">
                            <div class="card-header border-bottom-dashed align-items-center">
                                <h4 class="card-title m-0">
                                    {{ $t('pages.ecommerce.subscription.save.generalCardTitle') }}
                                </h4>
                            </div>
                            <div class="card-body px-9 pt-4">
                                <div class="row">
                                    <div class="col-md-6 fv-row mb-1 mt-3">
                                        <label class="required fs-6 fw-bold mb-2">{{ $t('pages.ecommerce.subscription.cols.subscribable_type') }}</label>
                                        <el-form-item prop="subscribable_type" :rules="$validation.getMessage(['required'])">
                                            <el-select v-model="form.data.subscribable_type" :disabled="form.updateStatus" class="w-100" :placeholder="$t('common.chooseSelect')" @change="onSubscribableTypeChange">
                                                <el-option value="COMPANY" :label="$t('pages.ecommerce.subscription.subscribable_types.COMPANY')"></el-option>
                                                <el-option value="CUSTOMER" :label="$t('pages.ecommerce.subscription.subscribable_types.CUSTOMER')"></el-option>
                                            </el-select>
                                        </el-form-item>
                                    </div>

                                    <div class="col-md-6 fv-row mb-1 mt-3">
                                        <label class="required fs-6 fw-bold mb-2">{{ $t('pages.ecommerce.subscription.cols.subscribable') }}</label>
                                        <el-form-item prop="subscribable_id" :rules="$validation.getMessage(['required'])">
                                            <el-select v-model="form.data.subscribable_id" :disabled="form.updateStatus || !form.data.subscribable_type" filterable class="w-100" :placeholder="$t('common.chooseSelect')">
                                                <el-option v-for="item in subscribableOptions" :key="item.id" :value="item.id" :label="formatSubscribableLabel(item)"></el-option>
                                            </el-select>
                                        </el-form-item>
                                    </div>

                                    <div class="col-md-6 fv-row mb-1 mt-3">
                                        <label class="required fs-6 fw-bold mb-2">{{ $t('pages.ecommerce.subscription.cols.product') }}</label>
                                        <el-form-item prop="product_id" :rules="$validation.getMessage(['required'])">
                                            <el-select v-model="form.data.product_id" :disabled="form.updateStatus" filterable class="w-100" :placeholder="$t('common.chooseSelect')" @change="onProductChange">
                                                <el-option v-for="p in availableProducts" :key="p.id" :value="p.id" :label="p.name + (p.code ? ' (' + p.code + ')' : '')"></el-option>
                                            </el-select>
                                        </el-form-item>
                                    </div>

                                    <div class="col-md-6 fv-row mb-1 mt-3">
                                        <label class="fs-6 fw-bold mb-2">{{ $t('pages.ecommerce.subscription.cols.price') }}</label>
                                        <el-form-item prop="product_price_id">
                                            <el-select v-model="form.data.product_price_id" :disabled="form.updateStatus || !form.data.product_id" class="w-100" :placeholder="$t('common.chooseSelect')" clearable>
                                                <el-option v-for="pr in availablePrices" :key="pr.id" :value="pr.id" :label="formatPriceOption(pr)"></el-option>
                                            </el-select>
                                        </el-form-item>
                                    </div>

                                    <div class="col-md-6 fv-row mb-1 mt-3">
                                        <label class="fs-6 fw-bold mb-2">{{ $t('pages.ecommerce.subscription.cols.starts_at') }}</label>
                                        <el-form-item prop="starts_at" class="dtp-form-input">
                                            <DateTimePicker v-model="form.data.starts_at" :disabled="form.updateStatus" type="datetime" :seconds="true" :placeholder="$t('common.chooseDate')"/>
                                        </el-form-item>
                                    </div>

                                    <div class="col-md-6 fv-row mb-1 mt-3">
                                        <label class="fs-6 fw-bold mb-2">{{ $t('pages.ecommerce.subscription.cols.expires_at') }}</label>
                                        <el-form-item prop="expires_at" class="dtp-form-input">
                                            <DateTimePicker v-model="form.data.expires_at" :disabled="expiresAtDisabled" type="datetime" :seconds="true" :placeholder="$t('common.chooseDate')"/>
                                        </el-form-item>
                                        <small v-if="form.data.is_trial" class="text-muted d-block mt-1">{{ $t('pages.ecommerce.subscription.expires_help.trial') }}</small>
                                        <small v-else-if="!isRecurringPrice" class="text-muted d-block mt-1">{{ $t('pages.ecommerce.subscription.expires_help.oneshot') }}</small>
                                    </div>

                                    <div class="col-md-6 fv-row mb-1 mt-3">
                                        <label class="fs-6 fw-bold mb-2">{{ $t('pages.ecommerce.subscription.cols.is_trial') }}</label>
                                        <el-form-item prop="is_trial">
                                            <el-select v-model="form.data.is_trial" :disabled="form.updateStatus" class="w-100" :placeholder="$t('common.chooseSelect')">
                                                <el-option :value="false" :label="$t('common.no')"></el-option>
                                                <el-option :value="true" :label="$t('common.yes')"></el-option>
                                            </el-select>
                                        </el-form-item>
                                    </div>

                                    <div class="col-md-6 fv-row mb-1 mt-3" v-if="form.data.is_trial">
                                        <label class="fs-6 fw-bold mb-2">{{ $t('pages.ecommerce.subscription.cols.trial_ends_at') }}</label>
                                        <el-form-item prop="trial_ends_at" class="dtp-form-input">
                                            <DateTimePicker v-model="form.data.trial_ends_at" :disabled="form.updateStatus" type="datetime" :seconds="true" :placeholder="$t('common.chooseDate')"/>
                                        </el-form-item>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="col-md-3">
                        <div>
                            <div class="card">
                                <div class="card-body">
                                    <div class="fv-row mb-1 mt-3" v-if="form.updateStatus">
                                        <label class="fs-6 fw-bold mb-2">{{ $t('pages.ecommerce.subscription.cols.status') }}</label>
                                        <div>
                                            <span :class="statusBadgeClass(form.data.status_id)" class="badge fs-7 px-3 py-2">{{ statusLabel(form.data.status_id) }}</span>
                                        </div>
                                    </div>

                                    <div class="fv-row mb-1 mt-3">
                                        <label class="fs-6 fw-bold mb-2">{{ $t('pages.ecommerce.subscription.cols.auto_renew') }}</label>
                                        <el-form-item prop="auto_renew">
                                            <el-select v-model="form.data.auto_renew" :disabled="!isRecurringPrice" class="w-100" :placeholder="$t('common.chooseSelect')">
                                                <el-option :value="true" :label="$t('common.yes')"></el-option>
                                                <el-option :value="false" :label="$t('common.no')"></el-option>
                                            </el-select>
                                        </el-form-item>
                                    </div>

                                    <div class="fv-row mb-1 mt-3">
                                        <label class="fs-6 fw-bold mb-2">{{ $t('pages.ecommerce.subscription.cols.grace_period_days') }}</label>
                                        <el-form-item prop="grace_period_days">
                                            <el-input v-model="form.data.grace_period_days" oninput="value=value.replace(/[^0-9]/g,'')"/>
                                        </el-form-item>
                                    </div>

                                    <div v-if="hasAnyAction" class="mt-5">
                                        <div class="separator border-bottom-dashed mb-4"></div>
                                        <label class="fs-6 fw-bold mb-3 d-block">{{ $t('pages.ecommerce.subscription.actions_title') }}</label>
                                        <div class="d-flex flex-column gap-2">
                                            <button v-if="canActivate" @click.prevent="onActivate" type="button" class="btn btn-sm btn-success">
                                                <i class="bi bi-check-lg me-1"></i>{{ $t('pages.ecommerce.subscription.actions.activate') }}
                                            </button>

                                            <button v-if="canExtend" @click.prevent="openExtendModal" type="button" class="btn btn-sm btn-primary">
                                                <i class="bi bi-calendar-plus me-1"></i>{{ $t('pages.ecommerce.subscription.actions.extend') }}
                                            </button>

                                            <button v-if="canSuspend" @click.prevent="onSuspend" type="button" class="btn btn-sm btn-warning">
                                                <i class="bi bi-pause-fill me-1"></i>{{ $t('pages.ecommerce.subscription.actions.suspend') }}
                                            </button>

                                            <button v-if="canResume" @click.prevent="onResume" type="button" class="btn btn-sm btn-info">
                                                <i class="bi bi-play-fill me-1"></i>{{ $t('pages.ecommerce.subscription.actions.resume') }}
                                            </button>

                                            <button v-if="canCancel" @click.prevent="onCancel" type="button" class="btn btn-sm btn-danger">
                                                <i class="bi bi-x-lg me-1"></i>{{ $t('pages.ecommerce.subscription.actions.cancel') }}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="fv-row mt-5">
                                <el-form-item class="mb-0">
                                    <button @click.prevent="onSubmit" :data-kt-indicator="form.loading ? 'on' : null" class="btn btn-lg btn-primary w-100" type="button">
                                        <span v-if="!form.loading" class="indicator-label">{{ $t("btn.save") }}</span>
                                        <span v-if="form.loading" class="indicator-progress">
                                            {{ $t("messages.wait") }}
                                            <span class="spinner-border spinner-border-sm align-middle ms-2"></span>
                                        </span>
                                    </button>
                                </el-form-item>
                            </div>
                        </div>
                    </div>
                </div>
            </el-form>
        </el-tab-pane>

        <el-tab-pane v-if="form.updateStatus" :label="$t('pages.ecommerce.subscription.save.tabs.modules')" name="modules">
            <custom-table
                :title="$t('pages.ecommerce.subscription.save.tabs.modules')"
                rowKey="id"
                :items="form.data.product?.modules || []"
                :loading="false"
                :emptyTableText="$t('pages.ecommerce.product.module.no_set_module')"
                :columns="moduleFields">
                <template v-slot:module_name="{ row: record }">
                    {{ record.module?.name }}
                </template>
                <template v-slot:daily_limit="{ row: record }">
                    {{ formatLimit(record.daily_limit) }}
                </template>
                <template v-slot:weekly_limit="{ row: record }">
                    {{ formatLimit(record.weekly_limit) }}
                </template>
                <template v-slot:monthly_limit="{ row: record }">
                    {{ formatLimit(record.monthly_limit) }}
                </template>
                <template v-slot:yearly_limit="{ row: record }">
                    {{ formatLimit(record.yearly_limit) }}
                </template>
            </custom-table>
        </el-tab-pane>

        <el-tab-pane v-if="form.updateStatus" :label="$t('pages.ecommerce.subscription.save.tabs.resources')" name="resources">
            <custom-table
                :title="$t('pages.ecommerce.subscription.save.tabs.resources')"
                rowKey="id"
                :items="form.data.product?.resources || []"
                :loading="false"
                :emptyTableText="$t('pages.ecommerce.product.resource.no_set_resource')"
                :columns="resourceFields">
                <template v-slot:resource_code="{ row: record }">
                    {{ $t('pages.ecommerce.product.resource.codes.' + record.resource_code) }}
                </template>
                <template v-slot:amount="{ row: record }">
                    {{ record.amount }} <span class="text-muted">{{ $t('pages.ecommerce.product.resource.units.' + record.resource_code) }}</span>
                </template>
            </custom-table>
        </el-tab-pane>

        <el-tab-pane v-if="form.updateStatus" :label="$t('pages.ecommerce.subscription.save.tabs.events')" name="events">
            <custom-table
                :title="$t('pages.ecommerce.subscription.save.tabs.events')"
                rowKey="id"
                :items="form.data.events || []"
                :loading="false"
                :emptyTableText="$t('pages.ecommerce.subscription.events.no_events')"
                :columns="eventFields">
                <template v-slot:event_name="{ row: record }">
                    <span class="badge badge-light-info">{{ eventLabel(record.event_id) }}</span>
                </template>
                <template v-slot:actor="{ row: record }">
                    {{ record.actor_type }}<span v-if="record.actor_id"> #{{ record.actor_id }}</span>
                </template>
                <template v-slot:created_at="{ row: record }">
                    {{ record.created_at ? $moment(record.created_at).format("DD.MM.YYYY HH:mm:ss") : '-' }}
                </template>
            </custom-table>
        </el-tab-pane>
    </el-tabs>

    <div class="modal fade" id="kt_modal_subscription_extend" ref="extendModal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered mw-650px">
            <div class="modal-content">
                <div class="modal-header">
                    <h2 class="fw-bolder">{{ $t('pages.ecommerce.subscription.extend.title') }}</h2>
                    <div data-bs-dismiss="modal" class="btn btn-icon btn-sm btn-active-icon-primary">
                        <span class="svg-icon svg-icon-1">
                            <inline-svg src="/media/icons/duotune/arrows/arr061.svg"/>
                        </span>
                    </div>
                </div>
                <el-form @submit.prevent="onExtendSubmit" :model="extend" ref="extendForm">
                    <div class="modal-body py-10 px-lg-17">
                        <small v-if="form.data.is_trial" class="text-warning d-block mb-3">{{ $t('pages.ecommerce.subscription.extend.trial_warning') }}</small>
                        <small v-else-if="form.data.status_id === 2" class="text-warning d-block mb-3">{{ $t('pages.ecommerce.subscription.extend.expired_warning') }}</small>

                        <div class="fv-row mb-3">
                            <label class="fs-6 fw-bold mb-2">{{ $t('pages.ecommerce.subscription.extend.presets') }}</label>
                            <div class="d-flex gap-2 flex-wrap">
                                <button v-for="d in [7, 30, 90, 365]" :key="d" type="button" class="btn btn-sm btn-light-primary" @click="extend.days = d">{{ d }} {{ $t('common.day') }}</button>
                            </div>
                        </div>

                        <div class="fv-row mb-3">
                            <label class="required fs-6 fw-bold mb-2">{{ $t('pages.ecommerce.subscription.extend.days_label') }}</label>
                            <el-form-item prop="days" :rules="$validation.getMessage(['required'])">
                                <el-input v-model="extend.days" :placeholder="$t('pages.ecommerce.subscription.extend.days_placeholder')" oninput="value=value.replace(/[^0-9]/g,'')"/>
                            </el-form-item>
                        </div>

                        <div v-if="extendPreviewDate" class="fv-row mb-1">
                            <label class="fs-6 fw-bold mb-2">{{ $t('pages.ecommerce.subscription.extend.preview_label') }}</label>
                            <div class="alert alert-success py-2 mb-0">{{ extendPreviewDate }}</div>
                        </div>
                    </div>

                    <div class="modal-footer flex-center">
                        <button :data-kt-indicator="extend.loading ? 'on' : null" class="btn btn-lg btn-primary" type="submit" :disabled="extend.loading">
                            <span v-if="!extend.loading" class="indicator-label">{{ $t("btn.save") }}</span>
                            <span v-if="extend.loading" class="indicator-progress">
                                {{ $t("messages.wait") }}
                                <span class="spinner-border spinner-border-sm align-middle ms-2"></span>
                            </span>
                        </button>
                    </div>
                </el-form>
            </div>
        </div>
    </div>
</template>

<script>
import PageHeader from "@/components/page-header/PageHeader.vue";
import CustomTable from "@/components/custom-table";
import DateTimePicker from "@/components/common/date-time-picker/DateTimePicker.vue";

const STATUS_CODE_MAP = {0: 'PENDING_PAYMENT', 1: 'ACTIVE', 2: 'EXPIRED', 3: 'CANCELLED', 4: 'SUSPENDED'};
const STATUS_BADGE_MAP = {
    0: 'badge-light-warning',
    1: 'badge-light-success',
    2: 'badge-light-danger',
    3: 'badge-light-dark',
    4: 'badge-light-warning',
};
const EVENT_CODE_MAP = {1: 'CREATED', 2: 'ACTIVATED', 3: 'CANCELLED', 4: 'SUSPENDED', 5: 'RESUMED', 6: 'EXTENDED', 7: 'EXPIRED', 8: 'SHRUNK'};

export default {
    name: "_id",
    components: {PageHeader, CustomTable, DateTimePicker},
    data() {
        return {
            tabModel: 'general',
            form: {
                title: '',
                loading: false,
                updateStatus: false,
                data: this.defaultFormData(),
            },
            extend: {
                loading: false,
                days: '',
            },
            moduleFields: [
                {name: this.$t("pages.ecommerce.product.module.cols.module_name"), scopedSlots: {customRender: "module_name"}},
                {name: this.$t("pages.ecommerce.product.module.cols.daily_limit"), scopedSlots: {customRender: "daily_limit"}},
                {name: this.$t("pages.ecommerce.product.module.cols.weekly_limit"), scopedSlots: {customRender: "weekly_limit"}},
                {name: this.$t("pages.ecommerce.product.module.cols.monthly_limit"), scopedSlots: {customRender: "monthly_limit"}},
                {name: this.$t("pages.ecommerce.product.module.cols.yearly_limit"), scopedSlots: {customRender: "yearly_limit"}},
            ],
            resourceFields: [
                {name: this.$t("pages.ecommerce.product.resource.cols.resource_code"), scopedSlots: {customRender: "resource_code"}},
                {name: this.$t("pages.ecommerce.product.resource.cols.amount"), scopedSlots: {customRender: "amount"}},
            ],
            eventFields: [
                {name: this.$t("pages.ecommerce.subscription.events.event"), scopedSlots: {customRender: "event_name"}},
                {name: this.$t("pages.ecommerce.subscription.events.actor"), scopedSlots: {customRender: "actor"}},
                {name: this.$t("common.createdAt"), scopedSlots: {customRender: "created_at"}},
            ],
        }
    },
    computed: {
        itemID() {
            return this.$route.params.id;
        },
        save() {
            return this.$store.state.ecommerce.subscription.save.data;
        },
        allProducts() {
            return this.$store.state.ecommerce.product.table?.data || [];
        },
        availableProducts() {
            if (!this.form.data.subscribable_type) return this.allProducts;
            return this.allProducts.filter(p => p.target_type === this.form.data.subscribable_type);
        },
        selectedProduct() {
            return this.allProducts.find(p => p.id === this.form.data.product_id) || null;
        },
        availablePrices() {
            return this.selectedProduct?.prices || [];
        },
        selectedPrice() {
            return this.availablePrices.find(p => p.id === this.form.data.product_price_id) || null;
        },
        isRecurringPrice() {
            return this.selectedPrice ? !!this.selectedPrice.is_recurring : true;
        },
        expiresAtDisabled() {
            return this.form.updateStatus || this.form.data.is_trial || !this.isRecurringPrice;
        },
        companies() {
            return this.$store.state.company.table?.data || [];
        },
        users() {
            return this.$store.state.user.table?.data || [];
        },
        subscribableOptions() {
            if (this.form.data.subscribable_type === 'COMPANY') return this.companies;
            if (this.form.data.subscribable_type === 'CUSTOMER') return this.users;
            return [];
        },
        canActivate() { return this.form.updateStatus && this.form.data.status_id === 0; },
        canCancel() { return this.form.updateStatus && [0, 1, 4].includes(this.form.data.status_id); },
        canSuspend() { return this.form.updateStatus && this.form.data.status_id === 1; },
        canResume() { return this.form.updateStatus && this.form.data.status_id === 4; },
        canExtend() { return this.form.updateStatus && [1, 2].includes(this.form.data.status_id); },
        hasAnyAction() {
            return this.canActivate || this.canCancel || this.canSuspend || this.canResume || this.canExtend;
        },
        extendPreviewDate() {
            if (!this.extend.days || parseInt(this.extend.days) <= 0) return '';
            const base = this.form.data.expires_at ? new Date(this.form.data.expires_at) : new Date();
            base.setDate(base.getDate() + parseInt(this.extend.days));
            return this.$moment(base).format("DD.MM.YYYY HH:mm");
        },
    },
    created() {
        if (this.itemID && !(this.itemID > 0)) {
            this.$router.push({path: "/ecommerce/subscription"});
        }
    },
    mounted() {
        this.$store.dispatch('ecommerce/product/get', {filterData: {page_limit: 9999}});

        if (this.itemID && this.itemID > 0) {
            this.setCurrentPageBreadcrumbs(this.sprintf(this.$t('pages.ecommerce.subscription.editSubscriptionPattern'), [this.itemID]), []);
            this.form.updateStatus = true;
            this.loadData();
        } else {
            this.setCurrentPageBreadcrumbs(this.$t('pages.ecommerce.subscription.newSubscription'), []);
        }
    },
    watch: {
        itemID(newValue) {
            if (newValue && newValue > 0) {
                this.form.updateStatus = true;
                this.loadData(newValue);
                this.setCurrentPageBreadcrumbs(this.sprintf(this.$t('pages.ecommerce.subscription.editSubscriptionPattern'), [newValue]), []);
            } else {
                this.resetForm();
                this.setCurrentPageBreadcrumbs(this.$t('pages.ecommerce.subscription.newSubscription'), []);
            }
        },
    },
    methods: {
        defaultFormData() {
            return {
                subscribable_type: undefined,
                subscribable_id: undefined,
                product_id: undefined,
                product_price_id: undefined,
                starts_at: null,
                expires_at: null,
                is_trial: false,
                trial_ends_at: null,
                auto_renew: true,
                grace_period_days: 0,
                metadata: null,
            };
        },
        resetForm() {
            this.tabModel = 'general';
            this.form.updateStatus = false;
            this.form.data = this.defaultFormData();
        },
        async loadData(id = this.itemID) {
            await this.$store.dispatch("ecommerce/subscription/show", {id});
            const data = this.save;
            this.form.data = {...data};

            if (data.subscribable_type === 'COMPANY') {
                this.$store.dispatch('company/get', {page: {pageSize: 9999}});
            } else if (data.subscribable_type === 'CUSTOMER') {
                this.$store.dispatch('user/get', {page: {pageSize: 9999}});
            }
        },
        statusLabel(statusId) {
            return this.$t('pages.ecommerce.subscription.statuses.' + (STATUS_CODE_MAP[statusId] || 'PENDING_PAYMENT'));
        },
        statusBadgeClass(statusId) {
            return STATUS_BADGE_MAP[statusId] || 'badge-light-secondary';
        },
        eventLabel(eventId) {
            return this.$t('pages.ecommerce.subscription.events.types.' + (EVENT_CODE_MAP[eventId] || 'CREATED'));
        },
        formatLimit(val) {
            if (val === null || val === undefined || val === '') {
                return this.$t('pages.ecommerce.product.module.unlimited');
            }
            return val;
        },
        formatPriceOption(price) {
            const periodLabel = price.period_type_id
                ? this.$t('pages.ecommerce.product.price.periods[' + (price.period_type_id - 1) + ']')
                : this.$t('pages.ecommerce.product.price.is_recurring_oneshot');
            const currency = price.currency?.code || '';
            return `${price.price} ${currency} - ${periodLabel}`;
        },
        formatSubscribableLabel(item) {
            if (this.form.data.subscribable_type === 'COMPANY') return item.name;
            const fn = item.firstname || '';
            const ln = item.lastname || '';
            return (fn + ' ' + ln).trim() || item.email || ('#' + item.id);
        },
        onSubscribableTypeChange(val) {
            this.form.data.subscribable_id = undefined;
            this.form.data.product_id = undefined;
            this.form.data.product_price_id = undefined;
            if (val === 'COMPANY') {
                this.$store.dispatch('company/get', {page: {pageSize: 9999}});
            } else if (val === 'CUSTOMER') {
                this.$store.dispatch('user/get', {page: {pageSize: 9999}});
            }
        },
        onProductChange() {
            this.form.data.product_price_id = undefined;
        },
        onSubmit() {
            this.$refs.dataForm.validate(async (valid) => {
                if (!valid) return false;

                this.form.loading = true;

                const payload = {...this.form.data};
                if (payload.grace_period_days === '' || payload.grace_period_days === null || payload.grace_period_days === undefined) {
                    payload.grace_period_days = 0;
                } else {
                    payload.grace_period_days = parseInt(payload.grace_period_days);
                }

                try {
                    if (this.form.updateStatus) {
                        const updatePayload = {
                            expires_at: payload.expires_at,
                            auto_renew: payload.auto_renew,
                            grace_period_days: payload.grace_period_days,
                            metadata: payload.metadata,
                        };
                        const response = await this.$store.dispatch('ecommerce/subscription/update', {
                            id: this.form.data.id,
                            formData: updatePayload,
                        });
                        if (response?.status) {
                            this.onResponse(response, () => this.loadData());
                        }
                    } else {
                        const response = await this.$store.dispatch('ecommerce/subscription/add', {
                            formData: payload,
                        });
                        if (response?.status) {
                            this.onResponse(response, () => {
                                this.$router.push({path: "/ecommerce/subscription/save/" + response.data.id});
                            });
                        }
                    }
                } finally {
                    this.form.loading = false;
                }
            });
        },
        onActivate() {
            this.eventBus.$emit('showConfirmDialog', {
                title: this.$t('pages.ecommerce.subscription.actions.activate'),
                message: this.$t('pages.ecommerce.subscription.confirms.activate'),
                confirmType: 'primary',
                onConfirm: async () => {
                    const response = await this.$store.dispatch('ecommerce/subscription/activate', {id: this.form.data.id});
                    if (response?.status) this.onResponse(response, () => this.loadData());
                },
            });
        },
        onCancel() {
            this.eventBus.$emit('showConfirmDialog', {
                title: this.$t('pages.ecommerce.subscription.actions.cancel'),
                message: this.$t('pages.ecommerce.subscription.confirms.cancel'),
                confirmType: 'danger',
                onConfirm: async () => {
                    const response = await this.$store.dispatch('ecommerce/subscription/cancel', {id: this.form.data.id});
                    if (response?.status) this.onResponse(response, () => this.loadData());
                },
            });
        },
        onSuspend() {
            this.eventBus.$emit('showConfirmDialog', {
                title: this.$t('pages.ecommerce.subscription.actions.suspend'),
                message: this.$t('pages.ecommerce.subscription.confirms.suspend'),
                confirmType: 'danger',
                onConfirm: async () => {
                    const response = await this.$store.dispatch('ecommerce/subscription/suspend', {id: this.form.data.id});
                    if (response?.status) this.onResponse(response, () => this.loadData());
                },
            });
        },
        onResume() {
            this.eventBus.$emit('showConfirmDialog', {
                title: this.$t('pages.ecommerce.subscription.actions.resume'),
                message: this.$t('pages.ecommerce.subscription.confirms.resume'),
                confirmType: 'primary',
                onConfirm: async () => {
                    const response = await this.$store.dispatch('ecommerce/subscription/resume', {id: this.form.data.id});
                    if (response?.status) this.onResponse(response, () => this.loadData());
                },
            });
        },
        openExtendModal() {
            this.extend = {loading: false, days: ''};
            this.showModal(this.$refs.extendModal);
        },
        onExtendSubmit() {
            this.$refs.extendForm.validate(async (valid) => {
                if (!valid) return false;
                if (!this.extend.days || parseInt(this.extend.days) < 1) return false;

                this.extend.loading = true;

                try {
                    const response = await this.$store.dispatch('ecommerce/subscription/extend', {
                        id: this.form.data.id,
                        days: parseInt(this.extend.days),
                    });
                    if (response?.status) {
                        this.onResponse(response, () => {
                            this.hideModal(this.$refs.extendModal);
                            this.loadData();
                        });
                    }
                } finally {
                    this.extend.loading = false;
                }
            });
        },
    }
}
</script>

